/**
 * UI affordances for asking the agent to suggest / rewrite / normalize
 * field values.
 *
 * Two surfaces:
 *   - {@link AiEditFieldButton}: per-field button that asks the agent
 *     to act on a single field. Lives next to the value in the Fields
 *     panel and shows a spinner while in-flight.
 *   - {@link AiEditBulkButton}: panel-level button that asks the agent
 *     to suggest values for every empty field on the doc at once.
 *
 * Both wrap {@link useSuggestEdits} and surface backend errors —
 * including the 429 rate-limit path — through `sonner` toasts and a
 * persisted error message under the button so the failure mode is
 * visible without requiring the toast to still be on screen.
 *
 * Acceptance / rejection of the resulting ``suggested_value`` flows
 * through the existing field editor UI in
 * ``$documentId.tsx``. We deliberately do NOT auto-accept proposals —
 * the central acceptance criterion is that AI output "never as silent
 * committed changes."
 */
import { Loader2, Sparkles, Wand2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import { useSuggestEdits } from '@/api/queries'
import type {
  AiEditAction,
  Field,
  SuggestEditsBody,
  SuggestEditsResponseBody,
} from '@/api/docfiller-types'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/**
 * Translate the backend error into a user-facing string. The router
 * sends a textual ``detail`` for all error paths, so we look for the
 * known rate-limit message first (so we can render "please retry
 * shortly" instead of a generic toast). Anything else falls back to the
 * error's own message — which {@link ApiError} populates from
 * ``detail``.
 */
function describeError(err: unknown): { message: string; isRateLimit: boolean } {
  const raw =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
      ? err
      : 'Something went wrong.'
  const lower = raw.toLowerCase()
  // The 429 body from the backend mentions "rate-limiting" — keep the
  // detection broad so a phrasing tweak doesn't silently lose the
  // friendly UI affordance.
  const isRateLimit = lower.includes('rate-limit') || lower.includes('rate limit')
  if (isRateLimit) {
    return {
      message: 'The AI provider is rate-limited. Please retry in a moment.',
      isRateLimit: true,
    }
  }
  if (lower.includes('ai provider error')) {
    return {
      message: 'The AI provider returned an error. Please retry.',
      isRateLimit: false,
    }
  }
  return { message: raw, isRateLimit: false }
}

function actionLabel(action: AiEditAction): string {
  switch (action) {
    case 'suggest':
      return 'Suggest a value'
    case 'rewrite':
      return 'Rewrite'
    case 'normalize':
      return 'Normalize'
  }
}

function actionShortLabel(action: AiEditAction): string {
  switch (action) {
    case 'suggest':
      return 'Suggested'
    case 'rewrite':
      return 'Rewritten'
    case 'normalize':
      return 'Normalized'
  }
}

interface AiEditFieldButtonProps {
  documentId: string
  field: Field
  /**
   * When true the AI button is disabled and labelled "Read-only".
   * Driven by the parent on viewer-role users so the affordance is
   * visible (so they know the feature exists) but can't accidentally
   * trip a 403 from the backend.
   */
  readOnly?: boolean
}

/**
 * Per-field "Ask AI" button. Reveals a small menu of action choices
 * keyed off whether the field currently has a value.
 */
export function AiEditFieldButton({
  documentId,
  field,
  readOnly = false,
}: AiEditFieldButtonProps) {
  const suggest = useSuggestEdits(documentId)
  const [lastError, setLastError] = useState<string | null>(null)

  const runAction = useCallback(
    async (action: AiEditAction, instructions: string) => {
      setLastError(null)
      try {
        const body: SuggestEditsBody = {
          action,
          scope: 'field',
          field_uuids: [field.uuid],
          instructions,
        }
        const result: SuggestEditsResponseBody = await suggest.mutateAsync(body)
        const proposal = result.proposals.find((p) => p.field_uuid === field.uuid)
        if (proposal) {
          toast.success(
            `${actionShortLabel(action)} a value — review below.`,
          )
        } else {
          const rejection = result.rejected.find((r) => r.field_uuid === field.uuid)
          toast.message(
            rejection ? `Skipped: ${rejection.reason}` : 'No suggestion produced.',
          )
        }
      } catch (err) {
        const { message } = describeError(err)
        setLastError(message)
        toast.error(message)
      }
    },
    [field.uuid, suggest],
  )

  const hasValue = !!(field.current_value && field.current_value.trim())
  const isSignature = field.kind === 'signature'

  // Signatures can't be AI-filled (the backend pre-rejects them too).
  // Hide the menu entirely instead of rendering disabled items — keeps
  // the panel tidy for forms with many signatures.
  if (isSignature) return null

  return (
    <div className="flex flex-col items-end gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 gap-1 px-2 text-xs"
            disabled={readOnly || suggest.isPending}
            aria-label="Ask AI for this field"
            data-testid={`ai-edit-${field.uuid}`}
          >
            {suggest.isPending ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
            ) : (
              <Wand2 className="size-3.5" aria-hidden />
            )}
            <span>Ask AI</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!hasValue && (
            <DropdownMenuItem
              data-testid={`ai-edit-${field.uuid}-suggest`}
              onSelect={() =>
                runAction('suggest', 'Use the document and any provided context.')
              }
            >
              <Sparkles className="mr-2 size-3.5" aria-hidden />
              {actionLabel('suggest')}
            </DropdownMenuItem>
          )}
          {hasValue && (
            <>
              <DropdownMenuItem
                data-testid={`ai-edit-${field.uuid}-rewrite`}
                onSelect={() =>
                  runAction('rewrite', 'Improve clarity and tone without changing the meaning.')
                }
              >
                <Wand2 className="mr-2 size-3.5" aria-hidden />
                {actionLabel('rewrite')}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid={`ai-edit-${field.uuid}-normalize`}
                onSelect={() =>
                  runAction('normalize', 'Format using the canonical form for this field type.')
                }
              >
                <Sparkles className="mr-2 size-3.5" aria-hidden />
                {actionLabel('normalize')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {lastError && (
        <p
          className="text-xs text-destructive"
          role="alert"
          data-testid={`ai-edit-${field.uuid}-error`}
        >
          {lastError}
        </p>
      )}
    </div>
  )
}

interface AiEditBulkButtonProps {
  documentId: string
  /** Number of currently-empty fields — drives the button label. */
  emptyFieldCount: number
  readOnly?: boolean
}

/**
 * Document-level "Suggest values" affordance. Asks the agent to
 * propose values for every blank field in one call.
 */
export function AiEditBulkButton({
  documentId,
  emptyFieldCount,
  readOnly = false,
}: AiEditBulkButtonProps) {
  const suggest = useSuggestEdits(documentId)
  const [lastError, setLastError] = useState<string | null>(null)

  const runBulk = useCallback(async () => {
    setLastError(null)
    try {
      const body: SuggestEditsBody = {
        action: 'suggest',
        scope: 'document',
        instructions: 'Use the document and any provided context.',
      }
      const result: SuggestEditsResponseBody = await suggest.mutateAsync(body)
      const filled = result.proposals.length
      const skipped = result.rejected.length
      if (filled > 0) {
        toast.success(
          `Suggested ${filled} value${filled === 1 ? '' : 's'} — review below.`,
        )
      } else if (skipped > 0) {
        toast.message('No new suggestions — see the rejected list for details.')
      } else {
        toast.message('No fields needed suggestions.')
      }
    } catch (err) {
      const { message } = describeError(err)
      setLastError(message)
      toast.error(message)
    }
  }, [suggest])

  if (emptyFieldCount <= 0) return null

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        type="button"
        size="sm"
        variant="soft"
        onClick={runBulk}
        disabled={readOnly || suggest.isPending}
        data-testid="ai-edit-bulk-suggest"
      >
        {suggest.isPending ? (
          <Loader2 className="animate-spin" aria-hidden />
        ) : (
          <Wand2 aria-hidden />
        )}
        Suggest values ({emptyFieldCount})
      </Button>
      {lastError && (
        <p
          className="text-xs text-destructive"
          role="alert"
          data-testid="ai-edit-bulk-error"
        >
          {lastError}
        </p>
      )}
    </div>
  )
}
